import { request } from "./http";

function unwrapPayload(payload) {
  if (payload && typeof payload === "object") {
    if ("item" in payload && payload.item !== undefined) return payload.item;
    if ("data" in payload && payload.data !== undefined) return payload.data;
  }
  return payload;
}

function normalizeEpisodeIds(values = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(values) ? values : []).forEach((value) => {
    const id = String(value ?? "").trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

export const playerApi = {
  bootstrapPlayer: async ({ episodeId, queue = [], positionSec = 0, locale = "" } = {}) => {
    if (!episodeId) throw new Error("episodeId is required");
    const raw = await request("/api/player/bootstrap", {
      method: "POST",
      json: {
        episode_id: String(episodeId),
        queue: normalizeEpisodeIds(queue),
        position_sec: Math.max(0, Number(positionSec) || 0),
        locale: String(locale || "").trim() || undefined,
      },
    });
    return unwrapPayload(raw) || null;
  },

  refreshSession: async ({ episodeId } = {}) => {
    if (!episodeId) throw new Error("episodeId is required");
    const raw = await request("/api/player/refresh-session", {
      method: "POST",
      json: {
        episode_id: String(episodeId),
      },
    });
    return unwrapPayload(raw) || null;
  },

  openPlayback: async ({
    assetId,
    projectId,
    episodeId,
    title,
    posterUrl,
    durationSeconds,
    userId,
    userEmail,
    userName,
  } = {}) => {
    const resolvedAssetId = String(assetId || "").trim();
    const resolvedEpisodeId = String(episodeId || "").trim();
    if (!resolvedAssetId && !resolvedEpisodeId) throw new Error("assetId or episodeId is required");
    const raw = await request("/api/player/open", {
      method: "POST",
      json: {
        assetId: resolvedAssetId || undefined,
        asset_id: resolvedAssetId || undefined,
        projectId: String(projectId || "").trim() || undefined,
        project_id: String(projectId || "").trim() || undefined,
        episodeId: resolvedEpisodeId || undefined,
        episode_id: resolvedEpisodeId || undefined,
        title: String(title || "").trim() || undefined,
        posterUrl: String(posterUrl || "").trim() || undefined,
        poster_url: String(posterUrl || "").trim() || undefined,
        durationSeconds: Number.isFinite(Number(durationSeconds)) ? Number(durationSeconds) : undefined,
        duration_seconds: Number.isFinite(Number(durationSeconds)) ? Number(durationSeconds) : undefined,
        userId: userId === null || userId === undefined ? undefined : String(userId),
        user_id: userId === null || userId === undefined ? undefined : String(userId),
        userEmail: String(userEmail || "").trim() || undefined,
        user_email: String(userEmail || "").trim() || undefined,
        userName: String(userName || "").trim() || undefined,
        user_name: String(userName || "").trim() || undefined,
      },
    });
    return unwrapPayload(raw) || null;
  },

  resolveBatch: async ({ episodeIds = [], includeDescription = false, includeTranscript = false, locale = "" } = {}) => {
    const ids = normalizeEpisodeIds(episodeIds);
    if (!ids.length) return { items: [] };
    const raw = await request("/api/player/resolve-batch", {
      method: "POST",
      json: {
        episode_ids: ids,
        include_description: Boolean(includeDescription),
        include_transcript: Boolean(includeTranscript),
        locale: String(locale || "").trim() || undefined,
      },
    });
    return unwrapPayload(raw) || { items: [] };
  },
};
